import type { UserId } from "./ids";

/** 11_AUTHORIZATION_POLICY §1 실측 10개. */
export const ROLE_CODES = [
  "SUPER_ADMIN", "ADMIN", "CONTENT_MANAGER", "COMMUNITY_MANAGER", "CS_MANAGER",
  "FINANCE_MANAGER", "READ_ONLY_ADMIN", "MODERATOR", "OFFICIAL_MANAGER", "USER",
] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

export const LEGACY_ROLE_ALIAS: Readonly<Record<string, RoleCode>> = {
  OWNER: "OFFICIAL_MANAGER",
  TRUSTED_USER: "USER",
};

export const VISIBILITIES = ["PRIVATE", "FOLLOWERS", "PUBLIC"] as const;
export type Visibility = (typeof VISIBILITIES)[number];

/**
 * GET /auth/me 실측 응답 (11_AUTHORIZATION_POLICY §5).
 * 서버는 roles[] 를 이미 보낸다. permissions[] 는 아직 없다.
 */
export interface CurrentUser {
  id: UserId;
  email: string;
  displayName: string;
  handle: string;
  /** 서버가 null 을 보낼 수 있습니다 (칼럼이 nullable). */
  handleSuffix: string | null;
  role: string;
  roles?: string[];
  permissions?: string[];
}

/**
 * AZ-02 해소: 단일 role 이 아니라 roles[] 를 합쳐서 판정한다.
 * Legacy 프론트는 roles[] 를 버려서 다중역할 사용자가 관리 UI 를 못 봤다.
 */
export function activeRoles(u: Pick<CurrentUser, "role" | "roles">): RoleCode[] {
  const raw = new Set<string>([u.role, ...(u.roles ?? [])].filter(Boolean));
  const out = new Set<RoleCode>();
  for (const r of raw) {
    const norm = LEGACY_ROLE_ALIAS[r] ?? r;
    if ((ROLE_CODES as readonly string[]).includes(norm)) out.add(norm as RoleCode);
  }
  if (out.size === 0) out.add("USER");
  return [...out];
}

const ADMIN_CONSOLE_ROLES: readonly RoleCode[] = [
  "SUPER_ADMIN", "ADMIN", "CONTENT_MANAGER", "COMMUNITY_MANAGER",
  "CS_MANAGER", "FINANCE_MANAGER", "READ_ONLY_ADMIN", "MODERATOR",
];

/** AZ-P1: 클라이언트 판정은 UI 힌트일 뿐이다. 서버가 다시 판정한다. */
export function canSeeAdminConsole(u: Pick<CurrentUser, "role" | "roles">): boolean {
  return activeRoles(u).some((r) => ADMIN_CONSOLE_ROLES.includes(r));
}

/**
 * 이름 옆에 덧붙일 handle. 덧붙일 것이 없으면 null 입니다.
 *
 * suffix 가 없는 계정은 서버의 `public_handle` 이 displayName 을 그대로
 * 돌려줍니다. 그대로 나란히 찍으면 같은 이름이 두 번 보여 사용자에게는
 * 버그로 읽힙니다. 두 플랫폼이 같은 판단을 쓰도록 여기에 둡니다.
 */
export function secondaryHandle(user: { displayName: string; handle: string }): string | null {
  return user.handle && user.handle !== user.displayName ? user.handle : null;
}
